import { useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

export type BookingStep = "service" | "time" | "contacts" | "review";

interface SheetState {
  sheetDepth?: number;
}

/**
 * The booking sheet lives in the URL (?book=<service>&step=…&at=<iso>): every
 * step is a history entry, so the system Back button walks back one step and
 * finally closes the sheet. Closing from the UI pops all sheet entries at once.
 */
export function useBookingNav() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const depth = (location.state as SheetState | null)?.sheetDepth ?? 0;

  const isOpen = params.has("book");
  const serviceId = params.get("book") || null;
  const step = (params.get("step") as BookingStep | null) ?? (serviceId ? "time" : "service");
  const at = params.get("at");
  const date = params.get("date");

  const go = useCallback(
    (next: Record<string, string | null>, opts: { replace?: boolean } = {}) => {
      const p = new URLSearchParams(params);
      for (const [k, v] of Object.entries(next)) {
        if (v === null) p.delete(k);
        else p.set(k, v);
      }
      navigate({ pathname: location.pathname, search: `?${p}` }, {
        replace: opts.replace,
        state: { sheetDepth: opts.replace ? depth : depth + 1 } satisfies SheetState,
      });
    },
    [navigate, location.pathname, params, depth],
  );

  const open = useCallback(
    (service?: string) => {
      const p = new URLSearchParams();
      p.set("book", service ?? "");
      p.set("step", service ? "time" : "service");
      navigate({ pathname: location.pathname, search: `?${p}` }, { state: { sheetDepth: 1 } satisfies SheetState });
    },
    [navigate, location.pathname],
  );

  const close = useCallback(() => {
    if (depth > 0) navigate(-depth);
    else navigate({ pathname: location.pathname, search: "" }, { replace: true });
  }, [depth, navigate, location.pathname]);

  return { isOpen, serviceId, step, at, date, go, open, close };
}
